import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Medal, Star, MapPin, Flame, Crosshair, Crown } from "lucide-react";
import api from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];
const TROPHY_IMG = "https://images.pexels.com/photos/6250995/pexels-photo-6250995.jpeg";

export default function Winners() {
  const [winners, setWinners] = useState([]);
  const [stars, setStars] = useState({});

  useEffect(() => {
    api.get("/winners").then((r) => setWinners(r.data)).catch(() => {});
    api.get("/stats/stars").then((r) => setStars(r.data)).catch(() => {});
  }, []);

  return (
    <main className="pt-28 pb-24 bg-league-ink min-h-screen text-white" data-testid="winners-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-league-red flex items-center gap-2">
              <Trophy size={14} /> Hall of Fame
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter mt-3 mb-4">
              PAST <span className="text-outline-white">CHAMPIONS</span>
            </h1>
          </div>
          <p className="text-white/40 text-xs font-semibold uppercase tracking-widest pb-2">Records via official CricHeroes tournament pages</p>
        </div>
        <div className="mb-14" />

        {winners[0] && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: EASE }}
            className="relative rounded-2xl overflow-hidden mb-14 border border-white/10"
            data-testid="winners-reigning-champion"
          >
            <img src={TROPHY_IMG} alt="Trophy celebration" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-league-deep/95 via-league-blue/70 to-transparent" />
            <div className="relative p-8 sm:p-14 max-w-xl">
              <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-league-red">Reigning Champions — Season {winners[0].season}.0</span>
              <h2 className="font-display text-4xl sm:text-6xl tracking-tight mt-3">{winners[0].champion}</h2>
              <div className="mt-6 space-y-2 text-white/80 text-sm font-medium">
                <p className="flex items-center gap-2"><Medal size={15} className="text-league-red" /> Runners-up — {winners[0].runner_up}</p>
                <p className="flex items-center gap-2"><Star size={15} className="text-league-red" /> Player of the Series — {winners[0].player_of_series}</p>
                <p className="flex items-center gap-2"><MapPin size={15} className="text-league-red" /> {winners[0].venue}</p>
              </div>
            </div>
          </motion.div>
        )}

        <div className="border-t border-white/10" data-testid="winners-board">
          <div className="hidden sm:grid grid-cols-12 gap-3 py-4 text-[11px] font-bold uppercase tracking-[0.25em] text-white/40">
            <span className="col-span-1">Season</span>
            <span className="col-span-1">Year</span>
            <span className="col-span-4">Champion</span>
            <span className="col-span-4">Runners-up</span>
            <span className="col-span-2">Player of the Series</span>
          </div>
          {winners.map((w, i) => (
            <motion.div
              key={w.id}
              initial={{ opacity: 0, x: -32 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.05, ease: EASE }}
              className="grid grid-cols-2 sm:grid-cols-12 gap-3 py-6 border-b border-white/10 items-center group"
              data-testid={`winner-entry-${w.season}`}
            >
              <span className="sm:col-span-1 font-oswald text-3xl font-semibold text-league-red">{w.season}.0</span>
              <span className="sm:col-span-1 font-oswald text-white/60 text-right sm:text-left">{w.year}</span>
              <span className="col-span-2 sm:col-span-4 font-oswald text-xl sm:text-2xl font-semibold transition-colors duration-200 group-hover:text-league-red">{w.champion}</span>
              <span className="col-span-2 sm:col-span-4 font-oswald text-xl sm:text-2xl font-semibold transition-colors duration-200 group-hover:text-league-red">{w.runner_up}</span>
              <span className="sm:col-span-2 text-sm text-white/60 flex items-center gap-3">
                {w.player_of_series !== "—" && w.player_of_series}
                {w.cricheroes_url && (
                  <a href={w.cricheroes_url} target="_blank" rel="noreferrer" data-testid={`winner-cricheroes-${w.season}`} className="text-league-red font-semibold text-xs uppercase tracking-widest hover:text-white transition-colors duration-200">
                    CricHeroes ↗
                  </a>
                )}
              </span>
              {stars[w.season] && (
                <div className="col-span-2 sm:col-span-12 mt-2 flex flex-wrap gap-x-6 gap-y-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3" data-testid={`winner-stars-${w.season}`}>
                  <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/30 self-center">Season Stars</span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-white/70">
                    <Flame size={12} className="text-league-red" /> {stars[w.season].batting.player} <span className="text-white/40">• {stars[w.season].batting.runs} runs</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-white/70">
                    <Crosshair size={12} className="text-league-red" /> {stars[w.season].bowling.player} <span className="text-white/40">• {stars[w.season].bowling.wickets} wkts</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-white/70">
                    <Crown size={12} className="text-league-red" /> {stars[w.season].mvp.player} <span className="text-white/40">• {stars[w.season].mvp.points} MVP pts</span>
                  </span>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </main>
  );
}
